import { action } from 'mobx';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import CheckCircle24 from '@honeybook/icons/dist/icons/CheckCircle24';
import { getCompanyTypes } from '~/utils/company.utils';
import type { RootStore } from '~/stores/root.store';
import {
    IFlowGalleryFilter,
    IImportedFlowTemplate,
    TSortBy
} from '~/components/FlowGallery/types/flow-gallery.types';
import { raceFunction } from '~/utils/data.utils';
import { TTrackFunction } from '~/common/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import { FlowRouteType } from '~/utils/flow.utils';
import { IEventProperties } from '~/services/analytics/analytics.service';
import { TModalStore } from '~/providers/modal.provider';
import { trackError } from '~/services/logger/error.utils';
import { goToFlow } from '~/components/Builder/Common/useFlowNavigation';
import { History } from 'history';
import { isReactFirst } from '~/utils/environment.utils';
import { getFlowUrl } from '~/components/Builder/Common/flow-url.utils';

export class FlowGalleryController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    @action.bound
    async loadFlowGallery(
        track: TTrackFunction,
        isMediumUp: boolean,
        isMSPEligable: boolean
    ) {
        const {
            flowGalleryUiStore: {
                isInitialLoading,
                setIsMounted,
                clearInitialLoading,
                loadIndustryFilters
            },
            configStore: {
                config: { company_type_enum }
            }
        } = this.rootStore;

        setIsMounted(true);

        try {
            // @ts-ignore
            const promises = [this.fetchTemplates()];
            if (isInitialLoading) {
                track('[Template gallery] push to gallery promises');
                promises.push(this.fetchTypeFilters());
                const companyTypes = getCompanyTypes(
                    company_type_enum,
                    isMSPEligable
                );
                track('[Template gallery] company types', {
                    types: companyTypes,
                    numOfTypes: companyTypes?.length
                });
                loadIndustryFilters(companyTypes);
            }

            await Promise.all(promises);
            track('[Template gallery] finished load promises');
            clearInitialLoading();

            this.trackFlowGalleryLoadedEvent(track, isMediumUp);
        } catch (error) {
            trackError({
                error,
                message:
                    '[Templates Gallery] Failed to fetch templates in loadFlowGallery',
                context: { templates_gallery: true }
            });
        }
    }

    @action.bound
    fetchTemplates = raceFunction((track: TTrackFunction) => {
        const {
            templatesService,
            flowGalleryUiStore,
            flowGalleryUiStore: {
                sortBy,
                searchQuery,
                singleFilterTag,
                activeFilterTags,
                activeTypeFilters,
                activeIndustryFilters,
                setIsLoading,
                loadTemplates
            },
            companyStore: { currCompany }
        } = this.rootStore;

        setIsLoading(true);

        return {
            try: () =>
                templatesService.fetchTemplates({
                    companyIndustry: currCompany?.company_type,
                    tags: activeFilterTags,
                    searchQuery,
                    industryTags: activeIndustryFilters?.map(x => x.key) || [],
                    sortBy
                }),
            set: templates => {
                loadTemplates(templates);
                track?.(AnalyticsEvents.FlowGalleryFilter, {
                    action: 'click',
                    search_query: searchQuery || null,
                    single_tag: singleFilterTag,
                    type_filters:
                        activeTypeFilters?.map(({ name }) => name) || null,
                    industry_filters:
                        activeIndustryFilters?.map(({ name }) => name) || null,
                    result_count: templates.length
                });
            },
            finally: () => setIsLoading(false),
            shouldResolve: () => flowGalleryUiStore.isMounted
        };
    });

    @action.bound
    async fetchTypeFilters() {
        const {
            flowGalleryService,
            flowGalleryUiStore: { loadTypeFilters }
        } = this.rootStore;

        const filters = await flowGalleryService.listFilters();

        loadTypeFilters(filters);
    }

    @action.bound
    clearAllFilters(track: TTrackFunction) {
        const {
            flowGalleryUiStore: { clearAllFilters }
        } = this.rootStore;

        clearAllFilters();
        this.fetchTemplates(track);
    }

    @action.bound
    toggleFilter(
        filter: IFlowGalleryFilter,
        shouldFetch: boolean,
        track: TTrackFunction
    ) {
        const {
            flowGalleryUiStore: { toggleFilter, setSingleTag }
        } = this.rootStore;

        toggleFilter(filter);
        setSingleTag(null);
        if (shouldFetch) {
            this.fetchTemplates(track);
        }
    }

    @action.bound
    setSingleTag(tag: string, track: TTrackFunction) {
        const {
            flowGalleryUiStore: { setSingleTag, clearAllFilters },
            quickPreviewUiStore: { resetQuickPreview }
        } = this.rootStore;

        clearAllFilters();
        setSingleTag(tag);
        this.fetchTemplates(track);
        resetQuickPreview();
        track(AnalyticsEvents.QuickPreviewTagClick, {
            action: 'click',
            filter_type: 'tag',
            filter_name: tag
        });
    }

    @action.bound
    setSortBy(sortBy: TSortBy, track: TTrackFunction) {
        const {
            flowGalleryUiStore: { setSortBy }
        } = this.rootStore;

        setSortBy(sortBy);
        track(AnalyticsEvents.FlowGallerySort, {
            action: 'click',
            sort_type: sortBy
        });

        this.fetchTemplates(track);
    }

    @action.bound
    async saveFlowTemplate(templateId: string, publishedTemplateId?: string) {
        const { templatesService, flowGalleryService } = this.rootStore;

        let _publishedTemplateId = publishedTemplateId;

        try {
            if (!_publishedTemplateId) {
                const galleryFlow = await flowGalleryService.downloadFlow({
                    flowId: templateId
                });
                _publishedTemplateId = galleryFlow?.publishedTemplateId;
            } else {
                flowGalleryService.downloadFlow({ flowId: templateId });
            }

            return await templatesService.downloadTemplate(
                _publishedTemplateId
            );
        } catch (error) {
            trackError({
                error,
                message: '[Templates Gallery]: Failed to save flow template',
                context: { templates_gallery: true }
            });
        }
    }

    @action.bound
    goToFlow(
        template: IImportedFlowTemplate,
        goToFlowType: FlowRouteType,
        track: TTrackFunction,
        history: History
    ) {
        const { injectorService } = this.rootStore;
        if (isReactFirst()) {
            history.push(
                getFlowUrl({
                    flowId: template._id,
                    forceRoute: goToFlowType
                })
            );
        } else {
            goToFlow({
                flowId: template._id,
                FlowService: injectorService.get('FlowService'),
                useAngularNavigation: true,
                forceRoute: goToFlowType
            });
        }
        track(AnalyticsEvents.GoToFlowTemplate, {
            action: 'click',
            type: 'flow',
            template_name: template.title
        });
    }

    getImportTemplateToastText() {
        return `You can find the template in “My Templates” under “Saved templates“`;
    }

    @action.bound
    async quickSaveFromPreview(
        templateId: string,
        publishedTemplateId: string,
        isMediumUp: boolean,
        track: TTrackFunction,
        publishedTemplateTitle: string,
        history: History
    ) {
        const {
            toastStore,
            quickPreviewUiStore: { source }
        } = this.rootStore;

        const importedTemplate = await this.saveFlowTemplate(
            templateId,
            publishedTemplateId
        );

        const analyticsEventProperties: IEventProperties = {
            action: 'click',
            name: publishedTemplateTitle || importedTemplate.title
        };

        if (source) {
            analyticsEventProperties.source = source;
            analyticsEventProperties.type = 'photographers';
        }

        track(
            AnalyticsEvents.FlowGalleryImportPublicFlow,
            analyticsEventProperties
        );

        toastStore.add({
            id: importedTemplate._id,
            type: ToastType.Success,
            title: `${importedTemplate.title} was successfully saved!`,
            autoDismissMs: 0,
            text: isMediumUp
                ? this.getImportTemplateToastText()
                : 'To edit this template, visit your desktop or the HoneyBook mobile app.',
            actions: [
                {
                    size: 'sm',
                    theme: 'secondary-invisible-primary',
                    text: `View ${isMediumUp ? 'and Customize ' : ''}Template`,
                    onClick: () => {
                        if (isMediumUp) {
                            toastStore.remove(importedTemplate._id);
                        }
                        this.goToFlow(
                            importedTemplate,
                            isMediumUp
                                ? FlowRouteType.edit
                                : FlowRouteType.preview,
                            track,
                            history
                        );
                    }
                }
            ]
        });
    }

    @action.bound
    async saveAndEditFromPreview(
        templateId: string,
        publishedTemplateId: string,
        track: TTrackFunction,
        modalStore: TModalStore,
        history: History
    ) {
        const { toastStore } = this.rootStore;

        const importedTemplate = await this.saveFlowTemplate(
            templateId,
            publishedTemplateId
        );

        this.goToFlow(importedTemplate, FlowRouteType.edit, track, history);

        track(AnalyticsEvents.FlowGalleryCustomizePublicFlow, {
            action: 'click',
            name: importedTemplate.title
        });

        toastStore.add({
            id: importedTemplate._id,
            type: ToastType.Success,
            icon: CheckCircle24,
            autoDismissMs: 0,
            title: 'Template saved successfully!',
            text: this.getImportTemplateToastText(),
            actions: [
                {
                    size: 'sm',
                    theme: 'secondary-invisible-primary',
                    text: 'Save time and apply your default theme',
                    onClick: () => {
                        modalStore.openGlobalModal('FlowThemeModalWrapper', {});
                        toastStore.remove(importedTemplate._id);
                        track(AnalyticsEvents.BuilderOpenFlowThemeModal, {
                            action: 'action'
                        });
                    }
                }
            ]
        });
    }

    @action.bound
    resetFlowGallery() {
        const {
            quickPreviewUiStore: { resetQuickPreview },
            flowGalleryUiStore: { resetFlowGallery }
        } = this.rootStore;

        resetQuickPreview();
        resetFlowGallery();
    }

    private trackFlowGalleryLoadedEvent(
        track: TTrackFunction,
        isMediumUp: boolean
    ) {
        const {
            userStore: {
                currUser: {
                    ab_tests: {
                        gallery_push_to_pros,
                        simplified_create_template
                    }
                }
            },
            uiPersistenceStore
        } = this.rootStore;

        const analyticsEventProperties: IEventProperties = {
            action: 'load'
        };

        /** AB Test - Gallery to pros */
        const shouldHideGalleryToProsBanner = uiPersistenceStore.get(
            'galleryPushToPros.isHideBanner',
            false
        );

        if (
            isMediumUp &&
            !!gallery_push_to_pros &&
            !shouldHideGalleryToProsBanner
        ) {
            analyticsEventProperties.push_to_pro_banner_variant =
                gallery_push_to_pros;
        }

        /** AB Test - Vertical templates */
        if (simplified_create_template) {
            analyticsEventProperties.simplified_create_template =
                simplified_create_template;
        }

        track(AnalyticsEvents.FlowGalleryLoaded, analyticsEventProperties);
    }

    get shouldHidePushToGalleryBanner() {
        const { uiPersistenceStore } = this.rootStore;

        return uiPersistenceStore.get(
            'dismissedPushToGalleryBanner.value',
            false
        );
    }
}
